import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  signal,
  computed,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslationService } from '../../../services/translation.service';

interface TimeSlot {
  value: string;
  label: string;
  hour: number;
  minute: number;
}

@Component({
  selector: 'app-time-picker',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './time-picker.component.html',
  styleUrls: ['./time-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimePickerComponent),
      multi: true,
    },
  ],
})
export class TimePickerComponent implements ControlValueAccessor, OnInit {
  @Input() placeholder = 'Select time';
  @Input() disabled = false;
  @Input() required = false;
  @Input() label?: string;
  @Input() errorMessage?: string;
  @Input() hasError = false;
  @Input() startHour = 8; // 8 AM
  @Input() endHour = 18; // 6 PM
  @Input() intervalMinutes = 30; // 30-minute intervals
  @Input() format: '12h' | '24h' | 'auto' = 'auto'; // auto uses language detection

  @Output() timeChange = new EventEmitter<string>();

  private translationService = inject(TranslationService);
  protected value = signal<string>('');
  protected isDropdownOpen = signal<boolean>(false);

  private onChange = (_value: string) => {};
  private onTouched = () => {};

  ngOnInit() {
    // Set format based on language if auto
    if (this.format === 'auto') {
      const currentLang = this.translationService.getCurrentLanguage();
      this.format = currentLang === 'fr' ? '24h' : '12h';
    }
  }

  // Computed values
  displayValue = computed(() => {
    const value = this.value();
    return value ? this.formatTimeForDisplay(value) : '';
  });

  timeSlots = computed(() => {
    return this.generateTimeSlots();
  });

  toggleDropdown() {
    if (this.disabled) {
      return;
    }
    this.isDropdownOpen.update((open) => !open);
  }

  closeDropdown() {
    this.isDropdownOpen.set(false);
  }

  selectTime(timeSlot: TimeSlot) {
    this.value.set(timeSlot.value);
    this.onChange(timeSlot.value);
    this.onTouched();
    this.timeChange.emit(timeSlot.value);
    this.closeDropdown();
  }

  private generateTimeSlots(): TimeSlot[] {
    const slots: TimeSlot[] = [];
    const startMinutes = this.startHour * 60;
    const endMinutes = this.endHour * 60;

    for (let minutes = startMinutes; minutes <= endMinutes; minutes += this.intervalMinutes) {
      const hour = Math.floor(minutes / 60);
      const minute = minutes % 60;
      const value = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

      slots.push({
        value,
        label: this.formatTimeForDisplay(value),
        hour,
        minute,
      });
    }

    return slots;
  }

  private formatTimeForDisplay(time: string): string {
    if (!time) {
      return '';
    }

    const [hourStr, minuteStr] = time.split(':');
    const hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);

    if (this.format === '24h') {
      return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    } else {
      // 12h format
      const isPM = hour >= 12;
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const period = isPM ? 'PM' : 'AM';
      return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string | null): void {
    this.value.set(value || '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
